/* ---------------------------------------------------------------------
 * quick-view.js
 * Opens a product Quick View in a native <dialog>. Triggered by any
 * `[data-quick-view-trigger][data-quick-view-url]` (rendered by the
 * product-listing card button). Fetches `<product.url>?view=quick-view`
 * (the `templates/product.quick-view.liquid` alternate template, rendered
 * with `layout none`), adopts any stylesheet <link>s from the response,
 * extracts the `[data-quick-view-product]` wrapper, injects it, and calls
 * `window.SDProduct.hydrate` (product.js) to bind variant / price / form.
 *
 * Add-to-cart inside the modal is intercepted and POSTed to /cart/add.js
 * (AJAX) rather than a full-page form submit — so the customer stays put
 * instead of landing on /cart. The Rebuy Smart Cart app intercepts the
 * cart request and opens its drawer; the modal closes on success.
 * --------------------------------------------------------------------- */
(function () {
  'use strict';

  // The <script> is emitted per product-listing section and re-runs on every
  // shopify:section:load (theme editor). Without this guard each execution
  // would register another document click-listener and build another <dialog>,
  // so a single trigger click would open several stacked modals. Wire up once.
  if (window.__sdQuickViewInitialized) return;
  window.__sdQuickViewInitialized = true;

  var CLOSE_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  var dialog = null;
  var content = null;
  var body = null;
  var activeTrigger = null;
  var zoomOverlay = null;
  var zoomImage = null;
  var lastZoomTrigger = null;

  function buildDialog() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'quick-view-modal';
    dialog.setAttribute('scroll-lock', '');
    dialog.setAttribute('aria-label', 'Product quick view');
    dialog.innerHTML =
      '<div class="quick-view-modal__content">' +
      '<button type="button" class="quick-view-modal__close" aria-label="Close" data-qv-close>' +
      CLOSE_SVG +
      '</button>' +
      '<div class="quick-view-modal__body" data-qv-body></div>' +
      '<div class="quick-view-modal__zoom" data-qv-zoom-overlay hidden>' +
      '<button type="button" class="quick-view-modal__close quick-view-modal__zoom-close" aria-label="Close zoom" data-qv-zoom-close>' +
      CLOSE_SVG +
      '</button>' +
      '<img class="quick-view-modal__zoom-image" data-qv-zoom-image src="" alt="">' +
      '</div>' +
      '</div>';

    document.body.appendChild(dialog);
    content = dialog.querySelector('.quick-view-modal__content');
    body = dialog.querySelector('[data-qv-body]');
    zoomOverlay = dialog.querySelector('[data-qv-zoom-overlay]');
    zoomImage = dialog.querySelector('[data-qv-zoom-image]');

    dialog.querySelector('[data-qv-close]').addEventListener('click', closeModal);
    dialog.querySelector('[data-qv-zoom-close]').addEventListener('click', closeZoom);

    /* Click routing: zoom trigger (open), zoom backdrop (close), modal
       backdrop (close). Delegated so it survives per-product re-injection. */
    dialog.addEventListener('click', function (e) {
      if (e.target.closest('[data-qv-zoom]')) {
        openZoom();
        return;
      }
      if (e.target === zoomOverlay) {
        closeZoom();
        return;
      }
      if (e.target === dialog) closeModal();
    });

    /* Esc: if the zoom overlay is open, close it first rather than the
       whole modal (the dialog's own cancel would otherwise close both). */
    dialog.addEventListener('cancel', function (e) {
      if (zoomOverlay && !zoomOverlay.hidden) {
        e.preventDefault();
        closeZoom();
      }
    });

    /* Native close (Esc / .close()) — clear the body + restore trigger. */
    dialog.addEventListener('close', function () {
      closeZoom();
      body.innerHTML = '';
      if (activeTrigger) {
        activeTrigger.classList.remove('is-loading');
        activeTrigger.removeAttribute('aria-busy');
        activeTrigger = null;
      }
    });

    return dialog;
  }

  function closeModal() {
    if (dialog && dialog.open) dialog.close();
  }

  /* Enlarge the currently-displayed main image to fill the modal. */
  function openZoom() {
    var main = body.querySelector('[data-qv-main]');
    if (!main || !zoomOverlay || !zoomImage) return;
    lastZoomTrigger = body.querySelector('[data-qv-zoom]');
    zoomImage.src = main.currentSrc || main.src;
    zoomImage.alt = main.alt || '';
    zoomOverlay.hidden = false;
    /* Freeze the content scroll so the overlay covers the visible modal. */
    if (content) {
      content.scrollTop = 0;
      content.style.overflow = 'hidden';
    }
    var closeBtn = zoomOverlay.querySelector('[data-qv-zoom-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeZoom() {
    if (!zoomOverlay || zoomOverlay.hidden) return;
    zoomOverlay.hidden = true;
    zoomImage.removeAttribute('src');
    if (content) content.style.overflow = '';
    if (lastZoomTrigger) {
      lastZoomTrigger.focus();
      lastZoomTrigger = null;
    }
  }

  /* Pull any stylesheet the Quick View response declares that the current
     page is missing, so injected markup is styled (product.css, etc.). */
  function adoptStylesheets(doc) {
    var present = {};
    document.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
      present[link.href] = true;
    });
    doc.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
      if (present[link.href]) return;
      var clone = document.createElement('link');
      clone.rel = 'stylesheet';
      clone.href = link.href;
      document.head.appendChild(clone);
      present[link.href] = true;
    });
  }

  /* Thumbnail → main image swap (self-contained QV gallery). */
  function bindGallery(root) {
    var gallery = root.querySelector('[data-qv-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-qv-main]');
    var thumbs = gallery.querySelectorAll('[data-qv-thumb]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-qv-full');
        if (full && main) main.src = full;
        thumbs.forEach(function (t) {
          t.classList.remove('is-active');
          t.removeAttribute('aria-current');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  /* Show / clear an inline add-to-cart error inside the form. */
  function showFormError(form, message) {
    var el = form.querySelector('[data-qv-form-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'quick-view__form-error list-sm';
      el.setAttribute('data-qv-form-error', '');
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    el.textContent = message;
  }

  function clearFormError(form) {
    var el = form.querySelector('[data-qv-form-error]');
    if (el) el.remove();
  }

  /* Add-to-cart: intercept the injected product form and POST it to
     /cart/add.js instead of a full-page submit (which would navigate to
     /cart). On success the modal closes and the Rebuy Smart Cart — which
     intercepts cart requests — opens its drawer. On failure an inline
     message is shown and the button is restored. */
  function bindAddToCart(root) {
    var form = root.querySelector('[data-product-form]');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.dataset.qvSubmitting === 'true') return;
      form.dataset.qvSubmitting = 'true';

      var submit = form.querySelector('[data-product-form-submit]');
      var label = submit ? submit.querySelector('[data-product-cta-label]') : null;
      var originalLabel = label ? label.textContent : '';
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }
      if (label) label.textContent = 'Adding…';
      clearFormError(form);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              throw new Error(data && data.description ? data.description : 'Add to cart failed');
            }
            return data;
          });
        })
        .then(function () {
          // Rebuy catches the /cart/add request and opens its Smart Cart
          // drawer; close the quick view so the drawer isn't hidden behind
          // the dialog's top layer.
          closeModal();
        })
        .catch(function (err) {
          showFormError(form, err && err.message ? err.message : 'Sorry, we couldn’t add this item to your cart.');
        })
        .finally(function () {
          form.dataset.qvSubmitting = 'false';
          if (submit) {
            submit.disabled = false;
            submit.removeAttribute('aria-busy');
          }
          if (label) label.textContent = originalLabel;
        });
    });
  }

  function openModal(trigger) {
    var url = trigger.getAttribute('data-quick-view-url');
    if (!url) return;

    activeTrigger = trigger;
    trigger.classList.add('is-loading');
    trigger.setAttribute('aria-busy', 'true');

    buildDialog();
    body.innerHTML = '<div class="quick-view-modal__spinner" role="status" aria-label="Loading"></div>';
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Quick view request failed');
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        adoptStylesheets(doc);

        var node = doc.querySelector('[data-quick-view-product]');
        if (!node) throw new Error('Quick view content missing');

        body.innerHTML = '';
        body.appendChild(document.importNode(node, true));

        var root = body.querySelector('[data-product-section]');
        if (window.SDProduct && typeof window.SDProduct.hydrate === 'function' && root) {
          window.SDProduct.hydrate(root);
        }
        bindGallery(body);
        bindAddToCart(body);

        if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') {
          window.affirm.ui.refresh();
        }
      })
      .catch(function () {
        var pdp = url.split('?')[0];
        body.innerHTML =
          '<p class="quick-view-modal__error">Sorry, we couldn’t load this product. ' +
          '<a href="' + pdp + '">View the product page</a>.</p>';
      })
      .finally(function () {
        if (activeTrigger) {
          activeTrigger.classList.remove('is-loading');
          activeTrigger.removeAttribute('aria-busy');
        }
      });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-view-trigger]');
    if (!trigger) return;
    e.preventDefault();
    openModal(trigger);
  });
})();
